// src/auth/adapters/Auth0Adapter.tsx
import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import createAuth0Client, {
	Auth0Client,
	LogoutOptions as Auth0LogoutOptions
} from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/browser';
import { AuthContext } from '../AuthContext';
import {
	AuthContextType,
	AuthState,
	AuthUser,
	LoginOptions,
	LogoutOptions
} from '../types';
import { useStorageService, STORAGE_KEYS } from 'services/storage';
import { useLocationService } from 'services/location';

interface Auth0AdapterProps {
	children: ReactNode;
	domain: string;
	clientId: string;
	audience?: string;
	redirectUri?: string;
	onRedirectCallback?: (appState?: { targetUrl?: string }) => void;
}

export const Auth0Adapter: React.FC<Auth0AdapterProps> = ({
	children,
	domain,
	clientId,
	audience,
	redirectUri,
	onRedirectCallback
}) => {
	const storage = useStorageService();
	const location = useLocationService();
	const [auth0Client, setAuth0Client] = useState<Auth0Client | null>(null);
	const [state, setState] = useState<AuthState>({
		isAuthenticated: false,
		isLoading: true,
		user: null,
		error: null
	});

	// Initialisation d'Auth0
	useEffect(() => {
		const initAuth0 = async () => {
			try {
				// Vérifier si on a un token dans l'URL (bypass Auth0)
				const hasToken = location.hasSearchParam('token');
				if (hasToken) {
					setState({
						isAuthenticated: true,
						isLoading: false,
						user: { id: 'token-user' },
						error: null
					});
					return;
				}

				// Vérifier si on a un code de parrainage
				const hasCode = location.hasSearchParam('redeem');
				if (hasCode) {
					const foundCode = location.getSearchParam('redeem');
					if (foundCode) {
						storage.setItem(STORAGE_KEYS.REDEEM_CODE, foundCode);
					}
				}

				// API Auth0 v1.x
				// cacheLocation: 'memory' (not localStorage) so an XSS payload cannot
				// exfiltrate the access/id tokens. Page reloads rely on getTokenSilently()
				// against the Auth0 session cookie; the user re-authenticates silently
				// while their Auth0 session is still valid (no full redirect login).
				const client = await createAuth0Client({
					domain,
					client_id: clientId,
					redirect_uri: redirectUri || location.getOrigin(),
					audience,
					cacheLocation: 'memory'
				});

				setAuth0Client(client);

				// Non-production only: expose a token getter for the Playwright
				// suite. PR #57 moved the SDK to `cacheLocation: 'memory'`, which
				// removed the `@@auth0spajs@@` localStorage entry the e2e helpers
				// used to read. Tests now call `window.__e2eGetToken()` instead.
				// Return the raw id_token (what the AppSync OIDC authorizer
				// expects in the `authorization` header) — getTokenSilently()
				// returns the access_token, which AppSync rejects.
				// Guarded on VITE_APP_ENV so production bundles never expose it.
				if (import.meta.env.VITE_APP_ENV !== 'production') {
					(window as unknown as { __e2eGetToken?: () => Promise<string | undefined> }).__e2eGetToken =
						async () => {
							// Force a silent refresh to make sure the id_token is fresh.
							await client.getTokenSilently();
							const claims = await client.getIdTokenClaims();
							return claims?.__raw;
						};
				}

				// Gérer le callback de redirection
				if (
					location.hasSearchParam('code') &&
					location.hasSearchParam('state')
				) {
					const { appState } = await client.handleRedirectCallback();
					onRedirectCallback?.(appState);
				}

				const isAuthenticated = await client.isAuthenticated();
				let user: AuthUser | null = null;

				if (isAuthenticated) {
					const auth0User = await client.getUser();
					user = auth0User
						? {
							id: auth0User.sub || '',
							email: auth0User.email,
							name: auth0User.name,
							picture: auth0User.picture,
							...auth0User
						}
						: null;

					// Sentry tracking
					if (user) {
						Sentry.setUser({
							id: user.id,
							email: user.email,
							name: user.name
						});
					}
				}

				setState({
					isAuthenticated,
					isLoading: false,
					user,
					error: null
				});
			} catch (error) {
				console.error('Auth0 initialization error:', error);
				setState((prev) => ({
					...prev,
					isLoading: false,
					error: error as Error
				}));
			}
		};

		initAuth0();
	}, [domain, clientId, audience, redirectUri, onRedirectCallback, location, storage]);

	// Actions
	const login = useCallback(
		async (options?: LoginOptions) => {
			if (!auth0Client) return;
			await auth0Client.loginWithRedirect({
				appState: options?.appState
			});
		},
		[auth0Client]
	);

	const logout = useCallback(
		async (options?: LogoutOptions) => {
			if (!auth0Client) return;
			Sentry.setUser(null);
			// API Auth0 v1.x
			const auth0Options: Auth0LogoutOptions = {
				returnTo: options?.returnTo || location.getOrigin()
			};
			auth0Client.logout(auth0Options);
		},
		[auth0Client, location]
	);

	const getToken = useCallback(async (): Promise<string | null> => {
		if (!auth0Client) return null;
		try {
			const claims = await auth0Client.getIdTokenClaims();
			if (claims?.__raw) return claims.__raw;
			// Cached claims missing — force a silent refresh, which repopulates
			// the ID token cache, then read it again. Without this, AppSync
			// receives the literal string "null" as Authorization and rejects
			// with 401 even though the user is logged in.
			await auth0Client.getTokenSilently();
			const refreshed = await auth0Client.getIdTokenClaims();
			return refreshed?.__raw || null;
		} catch (e) {
			console.warn('[Auth0] getToken failed', e);
			return null;
		}
	}, [auth0Client]);

	const getIdTokenClaims = useCallback(async () => {
		if (!auth0Client) return null;
		const claims = await auth0Client.getIdTokenClaims();
		return claims || null;
	}, [auth0Client]);

	const contextValue: AuthContextType = {
		...state,
		login,
		logout,
		getToken,
		getIdTokenClaims
	};

	return (
		<AuthContext.Provider value={contextValue}>
			{children}
		</AuthContext.Provider>
	);
};
